<template>
    <v-dialog v-model="showJobDialog"
            scrollable
            persistent
            transition="dialog-bottom-transition"
            :fullscreen="$windowWidth < 960"
            min-width="80%"
            min-height="80%"
            width="auto">
        <v-card v-if="dialogJob">
            <v-sheet color="info" class="smart-dialog-details">
                <v-btn icon="mdi-close"
                       class="smart-floating-close"
                       size="small"
                       variant="text"
                       @click="showJobDialog = false"></v-btn>
                <div class="d-flex justify-space-between">
                    <div>
                        <h1 class="mb-0">{{ dialogJob.name }} <type-chip :type="dialogJob.jobStatus" entity="Job" :object="job" size="large"></type-chip> <active-task-status :job="dialogJob" size="large"></active-task-status></h1>
                        <contact-link :contact="dialogJob.contact"></contact-link>
                        <div v-if="dialogJob.customerNumber">{{ dialogJob?.customerNumber }}</div>
                    </div>
                    <div class="w-50">
                        <h1 class="mb-0 text-right">${{ numberWithCommas(dialogJob.jobSubTotal, 2) }}</h1>
                        <h3 class="mb-0 text-right" v-if="dialogJob.outstandingSubtotal != dialogJob.jobSubTotal && dialogJob.outstandingSubtotal != 0">
                            Outstanding  ${{ numberWithCommas(dialogJob.outstandingSubtotal, 2) }}
                            <v-chip v-if="remainingPercentage"
                                    variant="elevated"
                                    :prepend-icon="remainingPercentage > 0 ? 'mdi-list-status' : 'mdi-check-circle'"
                                    size="small"
                                    :color="remainingPercentage > 0 ? 'warning' : 'success'">{{ numberWithCommas(remainingPercentage, 0) }}%</v-chip>
                        </h3>
                    </div>
                </div>
                <div class="d-flex justify-space-between border-t-sm mt-1 bt-1">
                    <div v-if="dialogJob.dateReceived" class="mr-6">
                        <small><v-icon icon="mdi-calendar-range"></v-icon> Receive Date</small><br /><date-ago-display :date="dialogJob.dateReceived" :days-ago="dialogJob.daysSinceReceived" :countdown="false"></date-ago-display>
                    </div>
                    <div v-if="dialogJob.dateReceived" class="mr-6">
                        <small><v-icon icon="mdi-truck-delivery-outline"></v-icon> Target Receive Date</small><br />{{ $dayjs(dialogJob.dateReceiveEstimate).format("DD-MM-YYYY") }} <v-chip v-if="!dialogJob.dateReceived && $dayjs(dialogJob.dateReceiveEstimate).isBefore($dayjs(), 'day')" variant="flat" size="x-small" class="ml-1" color="red-darken-2">OVERDUE</v-chip>
                    </div>
                    <div>
                        <small><v-icon icon="mdi-palette"></v-icon> Colour</small><br />{{ dialogJob.colour?.name }}
                    </div>
                    <div>
                        <small><v-icon icon="mdi-package-variant"></v-icon> Est.Powder</small><br />{{ numberWithCommas(dialogJob.totalPowderRequired, 1) }} Kgs
                    </div>
                    <div v-if="dialogJob.status == $constants.jobs.JOBSTAT_LINE1 && !(dialogJob.totalRunTime && dialogJob.totalRunTime > 0)">
                        <small><v-icon icon="mdi-timeline-clock-outline"></v-icon> Est.Line 1</small><br />{{ numberWithCommas(dialogJob.l1Time, 1) }} mins
                    </div>
                    <div v-if="dialogJob.status == $constants.jobs.JOBSTAT_LINE2 && !(dialogJob.totalRunTime && dialogJob.totalRunTime > 0)">
                        <small><v-icon icon="mdi-timeline-clock-outline"></v-icon> Est.Line 2</small><br />{{ numberWithCommas(dialogJob.l2Time, 1) }} mins
                    </div>
                    <div class="text-orange-accent-1" v-if="dialogJob.activeTask && dialogJob.activeTask.assignedLocation">
                        <small><v-icon icon="mdi-map-marker-radius"></v-icon> Location</small><br />{{ dialogJob.activeTask.assignedLocation }}
                    </div>
                    <div class="text-orange-accent-1" v-if="(dialogJob.totalRunTime && dialogJob.totalRunTime > 0)">
                        <small><v-icon icon="mdi-timeline-clock-outline"></v-icon> Line Time</small><br />{{ dialogJob.totalRunTime }} mins
                    </div>
                </div>
            </v-sheet>
            <v-card-text>
                <v-row>
                    <v-col cols="8" md="9">
                        <v-row>
                            <v-col cols="12" md="6">
                                <table v-if="dialogJob.jobItems" class="w-100 detail-table text-caption mt-2">
                                    <tr>
                                        <th class="text-left">Description</th>
                                        <th class="text-right">Quantity</th>
                                    </tr>
                                    <tr v-for="item in dialogJob.jobItems" :key="item.id">
                                        <td class="text-left">{{ item.description }}</td>
                                        <td class="text-right">{{ numberWithCommas(item.quantity, 0) }}</td>
                                    </tr>
                                </table>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-row class="mb-1">
                                    <v-col cols="6">
                                        <v-timeline side="end">
                                            <v-timeline-item size="small" :dot-color="dialogJob.dateReceived != null ? 'success' : ''">
                                                <v-checkbox :model-value="dialogJob.dateReceived != null"
                                                            label="Received"
                                                            @change="updateSmartReceived"
                                                            :hide-details="true"
                                                            density="compact"></v-checkbox>
                                            </v-timeline-item>
                                            <v-timeline-item size="small" :dot-color="dialogJob.dateStripped != null ? 'success' : ''">
                                                <v-checkbox :model-value="dialogJob.dateStripped != null"
                                                            label="Stripped"
                                                            @change="updateSmartPreprocess($constants.jobs.PREPRO_STRIPPING)"
                                                            :hide-details="true"
                                                            density="compact"></v-checkbox>
                                            </v-timeline-item>
                                            <v-timeline-item size="small" :dot-color="dialogJob.dateBlasted != null ? 'success' : ''">
                                                <v-checkbox :model-value="dialogJob.dateBlasted != null"
                                                            label="Blasted"
                                                            @change="updateSmartPreprocess($constants.jobs.PREPRO_BLASTING)"
                                                            :hide-details="true"
                                                            density="compact"></v-checkbox>
                                            </v-timeline-item>
                                            <v-timeline-item size="small" :dot-color="dialogJob.datePretreatment != null ? 'success' : ''">
                                                <v-checkbox :model-value="dialogJob.datePretreatment != null"
                                                            label="Pretreated"
                                                            @change="updateSmartPreprocess($constants.jobs.PREPRO_PRETREAT)"
                                                            :hide-details="true"
                                                            density="compact"></v-checkbox>
                                            </v-timeline-item>
                                        </v-timeline>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field label="Target Completion Date"
                                                      type="date"
                                                      density="compact"
                                                      :hide-details="true"
                                                      @change="updateSmartJob({ path:'dateTargetCompletion', value:dialogJob.targetDate})"
                                                      v-model="dialogJob.targetDate"></v-text-field>
                                        <v-select v-model="dialogJob.jobTag"
                                                  label="Job Tag"
                                                  density="compact"
                                                  :clearable="true"
                                                  @update:modelValue="updateSmartJob({ path:'jobTag', value:dialogJob.jobTag})"
                                                  :items="jobTags">
                                        </v-select>
                                        <div v-if="dialogJob.activeTask && (dialogJob.activeTask.status == $constants.jobs.JOBSTAT_BLASTING || dialogJob.activeTask.status == $constants.jobs.JOBSTAT_STRIPPING)">
                                            <job-task-buttons-timer :job="dialogJob"></job-task-buttons-timer>
                                        </div>
                                    </v-col>
                                </v-row>
                                <table v-if="dialogJob.archivedJobTasks && dialogJob.archivedJobTasks.length > 0" class="w-100 detail-table text-caption mt-2">
                                    <tr>
                                        <th class="w-25">Completed Task</th>
                                        <th class="w-25">Date</th>
                                        <th class="w-25">Value</th>
                                    </tr>
                                    <tr v-for="archivedTask in dialogJob.archivedJobTasks" :key="archivedTask.id">
                                        <td>{{ archivedTask.name }} <entity-tag-chip :object="archivedTask" class="ml-1"></entity-tag-chip></td>
                                        <td><span v-if="archivedTask.targetStartDate">{{ $dayjs(archivedTask.targetStartDate).format('DD-MM-YYYY') }}</span></td>
                                        <td class="text-right"><span v-if="archivedTask.estimatedSubtotalCompleted">${{ numberWithCommas(archivedTask.estimatedSubtotalCompleted, 2) }}</span></td>
                                    </tr>
                                </table>
                                <div v-if="dialogJob.jobItemTasks && dialogJob.jobItemTasks.length > 0">
                                    <table class="w-100 detail-table text-caption my-2 task-item-table">
                                        <tr>
                                            <th class="text-left">Task Item</th>
                                            <th class="text-right">Quantity</th>
                                            <th v-if="dialogJob.contact.nuwall">Picked Stock</th>
                                            <th v-if="dialogJob.contact.nuwall">Stock → Strip</th>
                                        </tr>
                                        <tr v-for="taskItem in dialogJob.jobItemTasks" :key="taskItem.jobTaskId">
                                            <td>{{ taskItem.itemDescription }}</td>
                                            <td class="text-right">{{ taskItem.quantity }}</td>
                                            <td class="text-right" v-if="dialogJob.contact.nuwall">{{ taskItem.pickedQuantity }} <span v-if="taskItem.pickLength">({{taskItem.pickLength}}M)</span></td>
                                            <td class="text-right" v-if="dialogJob.contact.nuwall">{{ taskItem.stockStripQuantity }}</td>
                                        </tr>
                                    </table>
                                </div>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="4" md="3" class="smart-note-section">
                        <note-bar></note-bar>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="d-flex justify-space-between">
                <div>
                    <v-btn :to="'/'+$constants.entities.Job.ROUTE+dialogJob.jobId"
                           @click.stop
                           variant="tonal"
                           prepend-icon="mdi-arrow-bottom-right-thin-circle-outline"
                           color="primary">
                        Go to Job
                    </v-btn>
                    <v-btn :to="'/'+$constants.entities.Job.ROUTE+dialogJob.jobId+'/jobsheet/'+dialogJob.jobTaskId"
                           variant="outlined"
                           color="primary"
                           v-if="true"
                           prepend-icon="mdi-printer"
                           class="mr-1"
                           @click.stop>
                        Job Card
                    </v-btn>
                </div>
                <div>
                    <v-btn @click="openSmartJobReworkDialog"
                           v-if="dialogJob.jobStatus != $constants.jobs.JOBSTAT_SCHEDULE"
                           class="ml-1 mb-1"
                           prepend-icon="mdi-recycle"
                           variant="tonal"
                           color="red-darken-1">Rework</v-btn>

                    <v-btn @click="openSmartJobCompletePartialDialog"
                           class="ml-1 mb-1"
                           v-if="dialogJob.jobStatus != $constants.jobs.JOBSTAT_SCHEDULE && dialogJob.jobStatus < $constants.jobs.JOBSTAT_INVOICE && !dialogJob.archived"
                           prepend-icon="mdi-progress-check"
                           variant="tonal"
                           color="success">Partially Complete</v-btn>

                    <v-btn @click="openSmartJobCompleteDialog"
                           class="ml-1 mb-1"
                           v-if="dialogJob.jobStatus != $constants.jobs.JOBSTAT_SCHEDULE && dialogJob.jobStatus < $constants.jobs.JOBSTAT_INVOICE && !dialogJob.archived"
                           prepend-icon="mdi-check-circle"
                           variant="tonal"
                           color="success">Complete</v-btn>

                    <v-btn v-if="dialogJob.jobStatus >= $constants.jobs.JOBSTAT_INVOICE"
                           prepend-icon="mdi-check-circle"
                           class="ml-1 mb-1"
                           color="secondary"
                           variant="tonal"
                           disabled>Completed</v-btn>

                    <v-btn v-if="dialogJob.jobStatus == $constants.jobs.JOBSTAT_INVOICE"
                           prepend-icon="mdi-invoice-text-outline"
                           class="ml-1 mb-1"
                           variant="tonal"
                           color="info"
                           @click="updateSmartJobStatus({ job: dialogJob, status: $constants.jobs.JOBSTAT_COMP})">Invoiced</v-btn>

                    <v-btn v-if="dialogJob.jobStatus == $constants.jobs.JOBSTAT_COMP"
                           prepend-icon="mdi-check-circle"
                           class="ml-1 mb-1"
                           color="secondary"
                           variant="tonal"
                           disabled>Job Invoiced</v-btn>
                    <v-btn @click="showJobDialog = false" variant="tonal" class="ml-1 mb-1">Close</v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<style scoped lang="scss">
    @import '../../../styles/smart.scss';
</style>
<script>
    import { mapActions, mapState } from "vuex";
    import { formatSearchName } from "../../../mixins/searchMixins";
    import { numberWithCommas } from "../../../mixins/numberMixins";
    function initialState() {
        return {
            saving: false,
            loading: false
        };
    }
    export default {
        name: "SmartJobDialog",
        data: function () {
            return initialState();
        },
        mounted: function () {
        },
        props: {
            job: Object,
        },
        methods: {
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack",
                "updateSmartJob",
                "updateSmartJobStatus",
                "openSmartJobCompleteDialog",
                "openSmartJobCompletePartialDialog",
                "openSmartJobReworkDialog",
                "openSmartPreprocess",
                "saveSmartPreprocessed",
                "updateSmartReceived",
                "deleteSmartTime"
            ]),
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            formatSearchName,
            numberWithCommas,
            deleteTime(time) {
                this.$confirm(
                    {
                        message: 'Are you sure you want to delete this time entry? THIS CANNOT BE UNDONE.',
                        disableKeys: false,
                        auth: false,
                        button: {
                            no: 'No',
                            yes: 'Yes'
                        },
                        callback: confirm => {
                            if (confirm) {
                                this.deleteSmartTime({ job: this.dialogJob, time: time });
                            }
                        }
                    }
                )
            },
            updateSmartPreprocess(type) {
                let currentValue = null; let verb = '';
                if (type === this.$constants.jobs.PREPRO_PRETREAT) {
                    currentValue = this.dialogJob.datePretreatment;
                    verb = 'pretreated';
                } else if (type === this.$constants.jobs.PREPRO_STRIPPING) {
                    currentValue = this.dialogJob.dateStripped;
                    verb = 'stripped';
                } else if (type === this.$constants.jobs.PREPRO_BLASTING) {
                    currentValue = this.dialogJob.dateBlasted;
                    verb = 'blasted';
                }
                if (currentValue) {
                    // Remove pretreated
                    this.$confirm(
                        {
                            message: `Are you sure you want to set job as NOT ${verb}?`,
                            disableKeys: false,
                            auth: false,
                            button: {
                                no: 'No',
                                yes: 'Yes'
                            },
                            callback: confirm => {
                                if (confirm) {
                                    this.saveSmartPreprocessed({ job: this.dialogJob, date: '', location: '', supervisor: '', type: type });
                                }
                            }
                        }
                    )
                } else {
                    this.openSmartPreprocess({ job: this.dialogJob, type: type });
                }
            },
        },
        computed: {
            ...mapState({
                dialogJob: (state) => state.smart.dialogJob,
            }),
            showJobDialog: {
                get() {
                    return this.$store.state.smart.showJobDialog
                },
                set(value) {
                    this.$store.commit('setShowJobDialog', value)
                }
            },
            jobTags() {
                return this.$constants.jobs.tags.map(item => item.name);
            },
            remainingPercentage() {
                if (this.dialogJob) {
                    return (this.dialogJob.outstandingSubtotal / this.dialogJob.jobSubTotal) * 100;
                }
                return null;
            },
        }
    };
</script>